import { MarkRetest } from './MarkRetest';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Building, Package, User } from 'lucide-react';
import { tasksService } from '../services';
import type { Task, TaskStatus, WorkflowSummary } from '../types';
import { formatShortDate } from '../utils/dateUtils';
import { priorityLabel } from '../utils/presentation';
import '../pages/ActionsInbox.css';
import './WorkflowCard.css';

interface StepProps {
  workflow: WorkflowSummary;
  currentUserId?: string;
  onOpen?: (id: string) => void;
  onComplete?: (id: string) => Promise<void>;
  onStatusChange?: (id: string, status: TaskStatus) => Promise<void>;
  variant?: 'compact' | 'stepper';
  canMarkRetest?: boolean;
  onRetestMarked?: () => void | Promise<void>;
}

export function WorkflowSteps({ workflow, currentUserId, onOpen, onComplete, onStatusChange, variant = 'compact', onRetestMarked, canMarkRetest }: StepProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [transitions, setTransitions] = useState<Record<string, TaskStatus[]>>({});
  const canChangeStatus = !!onStatusChange;
  useEffect(() => {
    if (!canChangeStatus) return;
    let cancelled = false;
    setTransitions({});
    setError('');
    const owned = workflow.steps.filter(step => step.task_id && step.responsible_user_id === currentUserId && !['complete', 'cancelled', 'upcoming'].includes(step.status) && step.transition !== 'link_retest');
    if (!owned.length) return;
    tasksService.getTasks({task_ids: owned.map(step => step.task_id!), include_steps:true})
      .then(tasks => { if (!cancelled) setTransitions(Object.fromEntries(tasks.map(task => [task.id, task.allowed_transitions || []]))); })
      .catch(() => { if (!cancelled) setError('Could not load step actions. Open a step to try again.'); });
    return () => { cancelled = true; };
  }, [workflow.steps, currentUserId, canChangeStatus]);
  const currentIndex = workflow.steps.findIndex(step => !['complete', 'cancelled', 'upcoming'].includes(step.status));
  return <><ol className={`workflow-steps workflow-steps--${variant}`} aria-label="Workflow steps">
    {workflow.steps.map((step, index) => {
      const done = step.status === 'complete';
      const active = index === currentIndex;
      const ownedOpen = step.task_id && step.responsible_user_id === currentUserId && !['complete', 'cancelled', 'upcoming'].includes(step.status);
      const allowed = transitions[step.task_id || ''] || [];
      const nextStatus = onStatusChange
        ? (['acknowledged', 'started', 'complete'] as TaskStatus[]).find(status => allowed.includes(status) && (status !== 'complete' || step.can_complete !== false))
        : step.can_complete !== false ? 'complete' : undefined;
      const automatic = step.transition === 'link_retest';
      const actionLabel = !onStatusChange ? 'Mark done' : nextStatus === 'acknowledged' ? 'Acknowledge' : nextStatus === 'started' ? (step.status === 'blocked' ? 'Resume working' : 'Start working') : 'Mark complete';
      return <li key={step.task_id || `${step.key}-${index}`} className={done ? 'done' : active ? 'current' : 'upcoming'} aria-current={active ? 'step' : undefined}>
        <span className="workflow-step-icon" aria-label={done ? 'Done' : active ? 'Current' : 'Upcoming'}>{done ? '✓' : active ? '•' : ''}</span>
        <div>{step.task_id && onOpen ? <button className="workflow-link" onClick={() => onOpen(step.task_id!)}>{step.title}</button> : <span>{step.title}</span>}
          <small>{step.responsible_name || 'Awaiting assignment'}{step.due_by && ` · due ${formatShortDate(step.due_by)}`}</small>
        </div>
        {ownedOpen && !automatic && nextStatus && onComplete && <button className={`btn btn-${variant === 'stepper' ? 'primary' : 'secondary'} btn-sm`} disabled={!!busy} onClick={async () => {
          setBusy(step.task_id!); setError('');
          try { if (nextStatus === 'complete') await onComplete(step.task_id!); else await onStatusChange?.(step.task_id!, nextStatus!); } catch (err) { setError(err instanceof Error ? err.message : 'Could not complete this step.'); } finally { setBusy(null); }
        }}>{actionLabel}</button>}
        {automatic && (ownedOpen || (canMarkRetest && step.status === 'upcoming' && ['actions_issued', 'in_rectification', 'retest_ordered'].includes(workflow.incident_status))) && <MarkRetest incidentId={workflow.incident_id} onMarked={onRetestMarked} />}
      </li>;
    })}
  </ol>{error && <p role="alert">{error}</p>}</>;
}

export function WorkflowBreadcrumb({ task, onOpen }: { task: Task; onOpen?: (id: string) => void }) {
  const context = task.workflow_breadcrumb;
  if (!context || !task.parent_task_id) return null;
  const label = `Step ${context.position} of ${context.total} · ${context.parent_title}`;
  return onOpen ? <button className="workflow-link workflow-breadcrumb" onClick={event => { event.stopPropagation(); onOpen(task.parent_task_id!); }}>{label}</button> : <a className="workflow-breadcrumb" href={`${window.location.pathname.startsWith('/portal') ? '/portal' : ''}/actions?task=${task.parent_task_id}`}>{label}</a>;
}

export default function WorkflowCard({ task, currentUserId, onOpen, onComplete, onStatusChange, onRetestMarked, canMarkRetest }: Omit<StepProps, 'workflow'> & { task: Task }) {
  const [expanded, setExpanded] = useState(false);
  const workflow = task.workflow;
  if (!workflow) return null;
  const next = workflow.next_step;
  const priority = task.priority || 'critical';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = workflow.steps.some(step => step.due_by && !['complete', 'cancelled'].includes(step.status) && new Date(step.due_by) < today);
  return <article className={`task-card workflow-card priority-${priority}`} role="button" tabIndex={0} aria-label={task.description}
    onClick={() => onOpen?.(task.id)}
    onKeyDown={event => {
      if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
        event.preventDefault(); onOpen?.(task.id);
      }
    }}>
    <header className="workflow-card-heading">
      <AlertTriangle aria-label="Positive lab result" size={18} />
      <h4 className="task-description">{task.description}</h4>
      <span className={`risk-badge risk-${priority}`}>{priority === 'critical' && <AlertTriangle size={11} />}{priorityLabel(priority)}</span>
      {overdue && <span className="status-badge overdue">Overdue</span>}
      <button className="btn btn-ghost btn-sm workflow-expand" aria-label={expanded ? 'Hide steps' : 'Show steps'} aria-expanded={expanded}
        onClick={event => { event.stopPropagation(); setExpanded(!expanded); }}>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
    </header>
    <div className="task-meta-row">
      {task.client_name && <span className="task-meta-chip"><Building size={12} />{task.client_name}</span>}
      {task.facility_name && <span className="task-meta-chip">{task.facility_name}</span>}
      {task.asset_name && <span className="task-meta-chip"><Package size={12} />{task.asset_name}</span>}
      {next && <span className="task-meta-chip task-assignee-chip"><User size={12} />{next.responsible_name || 'Awaiting assignment'}</span>}
      <span className="workflow-progress">{workflow.steps_done} of {workflow.steps_total} steps
        <progress aria-label="Workflow progress" value={workflow.steps_done} max={workflow.steps_total} />
      </span>
      {next && <span className="workflow-next">Next: {next.title} · {next.responsible_name || 'Awaiting assignment'}{next.due_by && ` · due ${formatShortDate(next.due_by)}`}</span>}
    </div>
    {expanded && <div className="workflow-card-steps" onClick={event => event.stopPropagation()}>
      <WorkflowSteps canMarkRetest={canMarkRetest} onRetestMarked={onRetestMarked} workflow={workflow} currentUserId={currentUserId} onOpen={onOpen} onComplete={onComplete} onStatusChange={onStatusChange} />
    </div>}
  </article>;
}
